import { FC, useEffect, createContext, useContext, Key } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";

import { isDispatched, isPending } from "@ctra/utils";

import {
  Enterprise,
  EnterpriseAppState,
  SavedCards,
  MetricEntity,
  Filter,
  V3DisplayFilter,
  ISODuration,
  ChartType,
  DataDescriptorEntity,
  PageEntity,
  DataFilterCollection,
  DisplayFilterCollection,
  CardPayload,
  PageResponse
} from "@ctra/api";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";

import { useFarm } from "@farms";
import { useDataDictionary } from "@base";

export type TargetKey = React.MouseEvent | React.KeyboardEvent | string;
type Pages = ReturnType<typeof Enterprise.entities.getLayoutOfFarm>;
export type InternalisedCard = Pages[number]["cards"][number];

type ContextType = {
  savedCharts: Pages;
  api: {
    addTab: (groupName: string) => void;
    updateTabOrder: (order: Array<Key>) => void;
    removeTab: (targetKey: TargetKey) => void;
    renameTab: (targetKey: TargetKey, newName: string) => void;
    saveChart: (
      variantID: MetricEntity["id"][],
      pageID: PageEntity["id"],
      groupName: PageEntity["name"],
      meta: {
        dateRange: [ISODuration] | [string, string];
        bucketSize: ISODuration;
        chartType: ChartType;
        dataFilters: DataFilterCollection;
        displayFilters?: DisplayFilterCollection;
        clientParams?: Record<string, string>;
      }
    ) => void;
    saveChartChanges: (pageID: PageEntity["id"], card: InternalisedCard) => void;
    removeChart: (id: InternalisedCard["id"], pageID: PageEntity["id"]) => void;
    updateChartOrder: (pageID: PageEntity["id"], order: Array<Key>) => void;
  };
  meta: {
    isLoading: boolean;
    isUpdating: boolean;
    tabsUpdating: boolean;
  };
};

/**
 * Default SavedCharts Context
 */
const DefaultContext = createContext<ContextType>({
  savedCharts: [],
  api: {
    addTab: _.noop,
    removeTab: _.noop,
    renameTab: _.noop,
    updateTabOrder: _.noop,
    saveChart: _.noop,
    saveChartChanges: _.noop,
    removeChart: _.noop,
    updateChartOrder: _.noop
  },
  meta: {
    isLoading: false,
    isUpdating: false,
    tabsUpdating: false
  }
});

const {
  kpi: { displayName }
} = Content;

/**
 * Fetch saved charts for the given farm or the dashboard
 * @constructor
 */
export const _SavedChartsProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const { dataDescriptors } = useDataDictionary();
  const { t } = useTranslation();
  const { farm, site } = useFarm();

  /**
   * Either get saved charts for a farm or all farms i.e. main
   */
  const contextID = site?.id ? site.id : farm?.id ? _.toString(farm.id) : "main";

  /**
   * Get all the pages for the given farm (or the main dashboard)
   * @type {any[] | {cards: {id: string, variantIDs: any[], displayName: string | null | undefined, chartType: ChartType, dateRange: [string] | [string, string], bucketSize: string | undefined, displayFilters: any, dataFilters: any}[], id: string, groupName: string}[]}
   */
  const pages = useSelector((state: EnterpriseAppState) => {
    return Enterprise.entities.getLayoutOfFarm(state, {
      farmID: contextID
    });
  });

  /**
   * Reconstruct pages in a server spec format
   * @type {Array<PageResponse>}
   */
  const denormalizedPages = useSelector((state: EnterpriseAppState) => {
    return Enterprise.entities.getServerSpecLayoutOfFarm(state, {
      farmID: contextID
    });
  });

  /**
   * Tell whether the fetching action has been dispatched
   */
  const dispatched = useSelector<EnterpriseAppState, boolean>((state) =>
    isDispatched(state, SavedCards.types.FETCH_SAVED_CHARTS, {
      primaryValue: contextID
    })
  );

  /**
   * Tell whether the fetching action is pending
   */
  const isLoading = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, SavedCards.types.FETCH_SAVED_CHARTS, {
      primaryValue: contextID
    })
  );

  /**
   * Tell whether chart list is being updated
   */
  const isUpdating = useSelector<EnterpriseAppState, boolean>((state) =>
    _.some([isPending(state, SavedCards.types.UPDATE_CHARTS), isPending(state, SavedCards.types.ADD_CARD)])
  );

  /**
   * Tell whether tabs are being updated
   */
  const tabsUpdating = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, SavedCards.types.UPDATE_TABS)
  );

  const pageSize = _.size(pages);

  useEffect(() => {
    if (!dispatched && pageSize === 0) {
      dispatch(SavedCards.actions.fetchSavedCharts.start(contextID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatched, pageSize]);

  /**
   * Add a new tab
   * @param {string} groupName
   */
  const addTab = (groupName: string) => {
    if (_.isArray(pages)) {
      const clone = _.cloneDeep(pages);

      clone.push({
        name: groupName,
        cards: [] as typeof pages[number]["cards"]
      } as typeof pages[number]);

      dispatch(SavedCards.actions.addTab.start(contextID, groupName));
    }
  };

  /**
   * Remove a tab
   * @param {TargetKey} targetKey
   */
  const removeTab = (targetKey: TargetKey) => {
    const updatedPages = _.reject(denormalizedPages, ({ id }) => _.isEqual(id, targetKey));

    dispatch(SavedCards.actions.updateTabs.start(contextID, updatedPages));
  };

  /**
   * Change the order of the tabs
   * @param {Array<React.Key>} order
   */
  const updateTabOrder = (order: Array<Key>) => {
    const currentOrder = _.map(denormalizedPages, "id");

    if (!_.isEqual(order, currentOrder)) {
      const sortedTabs = _.sortBy(denormalizedPages, ({ id }) => {
        return _.findIndex(order, (key) => _.isEqual(key, id));
      });

      dispatch(SavedCards.actions.updateTabs.start(contextID, sortedTabs));
    }
  };

  /**
   * Change the name of the tab
   * @param {TargetKey} targetKey
   * @param {string} groupName
   */
  const renameTab = (targetKey: TargetKey, groupName: string) => {
    const updatedTabs = _.map(denormalizedPages, (group) => {
      return group.id === targetKey ? { ...group, groupName } : group;
    });

    dispatch(SavedCards.actions.updateTabs.start(contextID, updatedTabs));
  };

  /**
   * Save a chart to dashboard
   * @param {MetricEntity["id"]} variantIDCollection
   * @param {Group["id"]} pageID
   * @param {Group["groupName"]} groupName
   * @param {{dateRange: [ISODuration] | [string, string], bucketSize: ISODuration, chartType: string, dataFilters: Array<Filter>, displayFilters?: Array<V3DisplayFilter>}} meta
   */
  const saveChart: ContextType["api"]["saveChart"] = (
    variantIDCollection: DataDescriptorEntity["id"][],
    pageID: PageEntity["id"],
    groupName: PageEntity["name"],
    meta: {
      dateRange: [ISODuration] | [string, string];
      bucketSize: ISODuration;
      chartType: ChartType;
      dataFilters: Record<DataDescriptorEntity["id"], Array<Filter>>;
      displayFilters?: Record<DataDescriptorEntity["id"], Array<V3DisplayFilter>>;
      clientParams?: Record<string, string>;
    }
  ) => {
    const { chartType, dateRange, bucketSize, dataFilters, displayFilters, clientParams } = meta;
    const variants = _.map(variantIDCollection, (id) => dataDescriptors[id]);
    const typeNames = _.map(variants, "dataProperties.typeName");

    const metricNames = _.map(typeNames, (typeName) => {
      return t<string>(displayName(typeName), {
        case: null,
        variant: null,
        makeDefaultValue: true
      });
    });

    /**
     * New card
     * @type {{displayName: null, layers: {variantId: string, bucketSize: string, chartType: ChartType, dateRange: [string] | [string, string], displayFilters: Array<V3DisplayFilter> | undefined, dataFilters: Array<Filter> | null}[]}}
     */
    const construct: CardPayload = {
      displayName: _.join(metricNames, ", "),
      layers: _.map(variantIDCollection, (variantID) => ({
        variantId: variantID,
        bucketSize,
        chartType,
        dateRange,
        displayFilters: displayFilters?.[variantID],
        dataFilters: dataFilters[variantID] || null,
        clientParams
      }))
    };

    dispatch(SavedCards.actions.addCard.start(contextID, pageID, groupName, construct));
  };

  /**
   * Update existing chart state
   * @param {PageEntity["id"]} pageID
   * @param {InternalisedCard} card
   */
  const saveChartChanges: ContextType["api"]["saveChartChanges"] = (
    pageID: PageEntity["id"],
    card: InternalisedCard
  ) => {
    const clone = _.cloneDeep(denormalizedPages);
    const group = _.find(clone, ["id", pageID]);

    if (group) {
      const chartIndex = _.findIndex(group.cards, ["id", card.id]);

      if (chartIndex > -1) {
        const cardResponse = group.cards[chartIndex];
        const updated = Enterprise.entities.toCardResponse(card);

        group.cards[chartIndex] = {
          ...cardResponse,
          ...updated
        };
      }
    }

    dispatch(SavedCards.actions.updateCharts.start(contextID, clone));
  };

  /**
   * Save a chart to dashboard
   * @param {InternalisedCard["id"]} id
   * @param {PageEntity["id"]} pageID
   */
  const removeChart = (id: InternalisedCard["id"], pageID: PageEntity["id"]) => {
    const updatedPages = _.map(denormalizedPages, (page) =>
      _.isEqual(page.id, pageID)
        ? {
            ...page,
            cards: _.reject(page.cards, (card) => _.isEqual(card.id, id))
          }
        : page
    );

    dispatch(SavedCards.actions.updateCharts.start(contextID, updatedPages));
  };

  /**
   * Change the order of the charts
   * @param {Array<React.Key>} order
   * @param {Group["id"]} id
   */
  const updateChartOrder = (id: PageEntity["id"], order: Array<Key>) => {
    const page = _.find(denormalizedPages, { id });

    if (!!page) {
      const currentOrder = _.map(page.cards, "id");

      if (!_.isEqual(order, currentOrder)) {
        const sortedCharts = _.sortBy(page.cards, ({ id }) =>
          _.findIndex(order, (key) => _.isEqual(key, id))
        );

        const updatedGroup: PageResponse = { ...page, cards: sortedCharts };
        const reordered = _.map(denormalizedPages, (page) => (_.isEqual(page.id, id) ? updatedGroup : page));

        dispatch(SavedCards.actions.updateCharts.start(contextID, reordered));
      }
    }
  };

  return (
    <DefaultContext.Provider
      value={{
        savedCharts: pages,
        api: {
          addTab,
          removeTab,
          renameTab,
          updateTabOrder,
          saveChart,
          saveChartChanges,
          removeChart,
          updateChartOrder
        },
        meta: { isLoading, isUpdating, tabsUpdating }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const SavedChartsContext = {
  Provider: _SavedChartsProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for SavedCharts context
 * @returns
 */
export const useSavedCharts = (): ContextType => useContext<ContextType>(DefaultContext);
